import { useEffect, useRef } from 'react';

/**
 * Retro 80s perspective grid - Tron-style floor effect
 */
const RetroGrid = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let animationId: number;
    let offset = 0;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      // Grid settings
      const gridSize = 50;
      const perspectiveY = height * 0.6; // Horizon line
      const vanishingX = width / 2;

      // Animate offset for moving grid
      offset += 0.5;
      if (offset > gridSize) offset = 0;

      // Draw horizontal lines (receding to horizon)
      ctx.strokeStyle = 'rgba(255, 40, 145, 0.3)'; // Hot pink
      ctx.lineWidth = 1;

      for (let y = perspectiveY; y < height; y += gridSize / 2) {
        const depth = (y - perspectiveY) / (height - perspectiveY);
        const leftX = vanishingX - (width / 2) * depth;
        const rightX = vanishingX + (width / 2) * depth;

        ctx.beginPath();
        ctx.moveTo(leftX, y + offset);
        ctx.lineTo(rightX, y + offset);
        ctx.stroke();
      }

      // Draw vertical lines (converging to center)
      ctx.strokeStyle = 'rgba(0, 242, 254, 0.2)'; // Cyan
      const numVerticals = 20;

      for (let i = 0; i <= numVerticals; i++) {
        const t = i / numVerticals;
        const x = width * t;

        ctx.beginPath();
        ctx.moveTo(x, height);
        ctx.lineTo(vanishingX, perspectiveY);
        ctx.stroke();
      }

      // Sun gradient at horizon
      const gradient = ctx.createRadialGradient(
        vanishingX,
        perspectiveY,
        0,
        vanishingX,
        perspectiveY,
        200
      );
      gradient.addColorStop(0, 'rgba(255, 40, 145, 0.3)');
      gradient.addColorStop(0.5, 'rgba(139, 0, 139, 0.2)');
      gradient.addColorStop(1, 'transparent');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default RetroGrid;
